import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CameraIcon } from "lucide-react";
import AssetUploader from "@/components/upload";
import { IFile } from "@/types/upload";
import { useGeneral } from "./useGeneral";
import { useGetSingleWorkspace } from "@/api/workspace/queries";

export default function General() {
  const { updateWorkspace } = useGeneral();
  const [workspaceName, setWorkspaceName] = useState("");
  const { data: workspace, isLoading: workspaceLoading } =
    useGetSingleWorkspace();
  const [avatar, setAvatar] = useState({ url: "", id: 0 });

  const workspaceSlug = useMemo(() => {
    return workspaceName
      .toLowerCase()
      .replace(/ /g, "_")
      .replace(/[^\w-]+/g, "");
  }, [workspaceName]);

  const handleWorkspaceNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setWorkspaceName(e.target.value);
  };

  const handleAvatarChange = (file: IFile) => {
    if (file?.url) {
      setAvatar({ url: file.url, id: Number(file.id) });
    }
  };

  const handleSaveChanges = async () => {
    updateWorkspace({
      name: workspaceName === workspace?.workspace.name ? "" : workspaceName,
      slug: workspaceSlug === workspace?.workspace.slug ? "" : workspaceSlug,
      avatar: avatar.id,
    });
  };

  useEffect(() => {
    if (workspace?.workspace.avatar) {
      setAvatar({
        url: workspace.workspace.avatar,
        id: Number(workspace.workspace.avatar),
      });
      setWorkspaceName(workspace.workspace.name);
    }
  }, [workspace]);

  return (
    <div className="w-full">
      <div className="flex flex-col items-center space-y-2">
        <Avatar className="h-24 w-24">
          <AvatarImage src={avatar.url} alt="Workspace photo" />
          <AvatarFallback>WS</AvatarFallback>
        </Avatar>
        <div className="relative">
          <AssetUploader
            pure
            onChange={handleAvatarChange}
            fileTypes={["PNG", "JPEG", "JPG"]}>
            <p className="flex items-center justify-center cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              <CameraIcon className="w-4 h-4 mr-2" />
              Change Picture
            </p>
          </AssetUploader>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="workspace-name">Workspace Name</Label>
        <Input
          isLoading={workspaceLoading}
          id="workspace-name"
          defaultValue={workspace?.workspace.name}
          onChange={handleWorkspaceNameChange}
          placeholder="Enter workspace name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="workspace-slug">Workspace Slug</Label>
        <Input
          isLoading={workspaceLoading}
          id="workspace-slug"
          readOnly
          className="bg-muted"
          value={workspaceSlug || workspace?.workspace.slug}
        />
        <p className="text-sm text-muted-foreground">
          This is your workspace's unique identifier and cannot be changed
          directly.
        </p>
      </div>
      <Button className="w-full mt-6" onClick={handleSaveChanges}>
        Save Changes
      </Button>
    </div>
  );
}
